<template>
  <form
    id="customise-photo-form"
    autocomplete="off"
    class="customise-photo-form"
    @submit.prevent="submitForm"
  >
    <!-- Photo preview -->
    <section
      id="preview"
      class="preview-container p-4"
    >
      <PhotoPreview
        :photo-url="photo.thumbnail.url"
        :overlay-url="formData.style === StyleType.Event && eventImageOverlay ? eventImageOverlay : null"
        :orientation="photo.thumbnail.dimensions.height > photo.thumbnail.dimensions.width ? 'portrait' : 'landscape'"
      />
    </section>

    <section class="col-span-12 md:col-span-6 flex flex-col h-full overflow-hidden">
      <h2 class="bg-tertiary text-left px-4 sm:px-6 py-3 text-lg m-0">
        Premium Options
      </h2>

      <!-- Form inputs -->
      <div class="overflow-y-auto h-auto md:max-h-[485px]">
        <CustomisePhotoFormBlock title="Style">
          <template
            v-for="style in photoStyles"
            :key="style.value"
          >
            <RadioInput
              v-model="formData.style"
              :label="style.label"
              :value="style.value"
            />
          </template>
        </CustomisePhotoFormBlock>

        <CustomisePhotoFormBlock title="Delivery">
          <template
            v-for="format in deliveryTypes"
            :key="format.value"
          >
            <RadioInput
              v-model="formData.delivery"
              :label="format.label"
              :hint="format.hint"
              :value="format.value"
            />
          </template>
        </CustomisePhotoFormBlock>

        <CustomisePhotoFormBlock
          title="Size"
          class="border-b-0"
        >
          <template
            v-for="size in photoSizes"
            :key="size.value"
          >
            <RadioInput
              v-model="formData.size"
              :label="size.label"
              :hint="size.hint"
              :value="size.value"
            />
          </template>
        </CustomisePhotoFormBlock>
      </div>

      <section class="footer">
        <AppButton
          class="btn-cancel"
          :disabled="isSubmitting"
          type="button"
          @click="close"
        >
          Cancel
        </AppButton>

        <!--Add to cart button-->
        <AppButton
          :class="isPhotoInCart ? 'btn-gray' : 'btn-secondary'"
          class="btn-solid w-full p-0 max-w-60 text-base flex items-center divide-x divide-secondary-subtle gap-0"
          :disabled="isSubmitting || isPhotoInCart === true"
        >
          <p class="flex-1 py-2 flex items-center justify-center gap-1">
            <FontAwesomeIcon
              v-if="isSubmitting"
              :icon="faSpinnerThird"
              spin
              class="text-xl"
            />

            <template v-if="isSubmitting">
              {{ orderItem?.id ? 'Updating item' : 'Adding to cart' }}
            </template>

            <template v-else>
              {{ orderItem?.id ? 'Update item' : 'Add to cart' }}
            </template>
          </p>

          <span class="px-3 min-w-16">
            R{{ price?.price.toFixed(2) }}
          </span>
        </AppButton>
      </section>
    </section>
  </form>
</template>

<script setup>
import { computed, reactive, watch } from 'vue';

import { AppButton } from '@/components/app';
import { CustomisePhotoFormBlock } from '@/components/blocks';
import { RadioInput } from '@/components/forms/inputs';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { DeliveryType, DigitalSize, PhotoType, PrintSize, StyleType } from '@/const';
import { useCartStore } from '@/store';
import { PhotoPreview } from '@/components/galleries';

const isSubmitting = defineModel('isSubmitting');

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  orderItem: {
    type: Object,
    default: () => ({}),
  },
  event: {
    type: Object,
    default: () => ({}),
  },
  photo: {
    type: Object,
    default: () => ({}),
  },
  searchedBibNumber: {
    type: Number,
    default: null,
  },
});

const cartStore = useCartStore();

const formData = reactive({
  style: props.orderItem.style || (props.event.eventOverlayLandscape || props.event.eventOverlayPortrait ? StyleType.Event : StyleType.Plain),
  delivery: props.orderItem.delivery || DeliveryType.Digital,
  size: props.orderItem.size || DigitalSize.HighDefinition,
  quantity: props.orderItem.quantity || 1,
  bibNumber: null,
  event: props.event?.id || props.orderItem.event?.id,
  photo: props.photo?.id || props.orderItem.photo?.id,
});

// Race time will not be a style option for now
const photoStyles = [
  { label: 'Plain', value: StyleType.Plain },
  { label: 'Event Overlay', value: StyleType.Event },
  // { label: 'Race Time', value: StyleType.RaceTime },
];

const deliveryTypes = [
  { label: 'Digital', value: DeliveryType.Digital, hint: 'Immediately downloadable after payment' },
  { label: 'Print', value: DeliveryType.Print, hint: 'Print orders automatically include a free digital download' },
];

const digitalSizes = [
  { label: 'Standard', value: DigitalSize.Standard, hint: 'Great for sharing on social media' },
  {
    label: 'High Definition, Original Resolution',
    value: DigitalSize.HighDefinition,
    hint: 'Perfect for professional printing',
  },
];

const printSizes = [
  { label: 'Standard', value: PrintSize.Standard, hint: '10cm x 15cm, gloss' },
  { label: 'Jumbo', value: PrintSize.Jumbo, hint: '15cm x 20cm, gloss' },
  { label: 'A4', value: PrintSize.A4, hint: '21cm x 30cm, gloss' },
  { label: 'A3', value: PrintSize.A3, hint: 'Printed on stretched canvas frame' },
];

const photoSizes = computed(() => {
  if (formData.delivery === DeliveryType.Digital) {
    return digitalSizes;
  }

  return printSizes;
});

const submitForm = async () => {
  isSubmitting.value = true;

  if (props.orderItem.id) {
    delete (formData.bibNumber);
    delete (formData.event);
    delete (formData.photo);

    await cartStore.updateItem(formData, props.orderItem.id);
  } else {
    await cartStore.addItem(formData);
  }

  isSubmitting.value = false;
  emit('close');
  emit('confirm');
};

const close = () => {
  if (!isSubmitting.value) {
    emit('close');
  }
};

watch(() =>
  formData.delivery, (newVal) => {
  if (newVal === DeliveryType.Digital) {
    formData.size = DigitalSize.Standard;
    formData.quantity = 1;
  } else if (newVal === DeliveryType.Print) {
    formData.size = PrintSize.Jumbo;
  }
});

const isPhotoInCart = computed(() => {

  return cartStore.cart?.items?.some(
    order => order.photo?.ulid === props.photo?.ulid &&
      order.delivery === formData.delivery &&
      order.type === PhotoType.Single &&
      order.style === formData.style &&
      order.size === formData.size,
  );
});

const price = computed(() => {
  // When customising a photo from the cart page, there will be no `props.event`, so the prices from the `props.orderItem.event` will be used.
  let prices = isEmpty(props.event) ? props.orderItem.event.prices : props.event.prices;

  return prices?.find(price => price.delivery === formData.delivery && price.size === formData.size && price.style === formData.style);
});

const isEmpty = (obj) => {
  if (obj) {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false;
      }
    }
  }

  return true;
};

const eventImageOverlay = computed(() => {
  // Check if photo and its dimensions are valid.  Added defensive check.
  if (!props.photo || !props.photo.thumbnail.dimensions || !props.photo.thumbnail.dimensions.width || !props.photo.thumbnail.dimensions.height) {
    return null; // Or a default image URL or empty string
  }

  const isLandscape = props.photo.thumbnail.dimensions.width > props.photo.thumbnail.dimensions.height;
  const overlayKey = isLandscape ? 'eventOverlayLandscape' : 'eventOverlayPortrait';
  const overlayImage = props.orderItem?.event?.[overlayKey] || props.event[overlayKey];

  // Prioritize orderItem data if available and populated.
  if (overlayImage) {
    return overlayImage;
  }

  // Return null if no suitable overlay is found.
  return null;
});

</script>
